import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { getReviewStatus, submitReview, rewardDownloadUrl } from "@/lib/api";
import { useSite, useImages } from "@/context/SiteContent";

const LABELS = ["", "Not for me", "It was okay", "Good", "Really useful", "Changed how I think"];

const Stars = ({ value, onChange }) => {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div className="rv-stars" role="radiogroup" aria-label="Rating" data-testid="review-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button type="button" key={n} role="radio" aria-checked={value === n} className={n <= shown ? "on" : ""} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => onChange(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`review-star-${n}`}>
          <Star size={30} fill={n <= shown ? "currentColor" : "none"} />
        </button>
      ))}
      <span className="rv-star-label" data-testid="review-star-label">{LABELS[shown] || "Tap a star"}</span>
    </div>
  );
};

export default function Review() {
  const { token } = useParams();
  const { price, links } = useSite();
  const images = useImages();
  const [state, setState] = useState("loading");
  const [form, setForm] = useState({ rating: 0, quote: "", name: "", role: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reward, setReward] = useState(false);

  useEffect(() => {
    getReviewStatus(token).then((r) => { setReward(!!r.reward); setState(r.submitted ? "done" : "form"); }).catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return setError("Please choose a star rating.");
    if (form.quote.trim().length < 10) return setError("Please write at least one full sentence.");
    if (!form.name.trim()) return setError("Please add your first name.");
    setError(""); setBusy(true);
    try { const res = await submitReview(token, { ...form, quote: form.quote.trim(), name: form.name.trim(), role: form.role.trim() }); setReward(!!res.reward); setState("done"); }
    catch (err) { setError(err?.response?.data?.detail || "We could not save your review right now. Please try again shortly."); }
    finally { setBusy(false); }
  };

  return (
    <div className="pay-page rv-page" data-testid="review-page">
      <div className="pay-card rv-card">
        <Link to="/" className="pay-logo"><img alt="TransitionWorks" src="/tw-logo.png" /></Link>
        {state === "loading" && <div className="pay-spinner" />}
        {state === "invalid" && <>
          <span className="eyebrow">Link not recognised</span>
          <h1>This review link is not valid.</h1>
          <p className="lead">Review links are tied to a completed purchase. If you bought the eBook, please use the link from your email or contact us.</p>
          <div className="pay-actions"><Link className="btn btn-gold" to="/" data-testid="review-home-btn">Back to Campus2Corp</Link></div>
        </>}
        {state === "form" && <>
          <span className="eyebrow">One sentence, one minute</span>
          <h1>How is the journey going?</h1>
          <p className="lead">Thank you for reading <strong>From Campus to Corporate</strong>. What stood out? Your words may help the next graduate decide to start earlier.</p>
          <form className="rv-form" onSubmit={submit} noValidate data-testid="review-form">
            <Stars value={form.rating} onChange={(n) => setForm((f) => ({ ...f, rating: n }))} />
            <label className="form-field"><span>Your review *</span>
              <textarea rows={4} maxLength={600} placeholder="For example: The First 90 Days lesson changed how I asked questions in my first month." value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} data-testid="review-quote-input" /></label>
            <div className="form-grid">
              <label className="form-field"><span>First name *</span>
                <input placeholder="Your first name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="review-name-input" /></label>
              <label className="form-field"><span>Where you are <em>Optional</em></span>
                <input placeholder="Fresh graduate, Singapore" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="review-role-input" /></label>
            </div>
            <div className="form-error" data-testid="review-error">{error}</div>
            <button className="btn btn-gold" type="submit" disabled={busy} data-testid="review-submit-btn">{busy ? "Sending..." : "Send my review"}</button>
            <small className="rv-note">Only your first name, where you are and the words you write may appear on the website. Your email stays private.</small>
          </form>
        </>}
        {state === "done" && <>
          <div className="booking-success-icon">✓</div>
          <span className="eyebrow">Thank you</span>
          <h1>Your review is in.</h1>
          {reward
            ? <p className="lead">As a small thank-you, here is your bonus <strong>5Ps Reflection Sheet</strong>: one page, five prompts to revisit at every transition. We have emailed you a copy too.</p>
            : <p className="lead">Scott reads every one. If you know someone about to start their first job, the eBook is a one-time {price} and the free lessons on TikTok are always open.</p>}
          <div className="pay-actions">
            {reward && <a className="btn btn-gold" href={rewardDownloadUrl(token)} data-testid="review-reward-btn">Download your 5Ps Reflection Sheet</a>}
            <a className={reward ? "btn btn-light" : "btn btn-gold"} href={links.tiktok} target="_blank" rel="noopener noreferrer" data-testid="review-tiktok-btn">Follow @campustocorporate1</a>
            <Link className="btn btn-light" to="/" data-testid="review-home-btn">Back to Campus2Corp</Link>
          </div>
        </>}
        <img className="rv-cover" src={images.cover} alt="" />
      </div>
    </div>
  );
}
